var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Exclude, Type } from "class-transformer";
import { CreateDepartmentParams, CreateEmployeeParams, CreateVenueParams, GetCompanyParams, GetTimeEntriesParams, LoginParams, UpdateAccountParams, UpdateCompanyParams, UpdateDepartmentParams, UpdateEmployeeParams, UpdateVenueParams, } from "./api";
import { AccountDTO, SessionDTO, TransferVenueDTO } from "./api/rpc";
import { EmployeeFilters, matchesFilters, sortBy } from "./filters";
import { Localized } from "./localized";
import { CompanyEntity, Employee, TimeEntry, TimeEntryType, TimeSettings, } from "./model";
import { PERMISSIONS } from "./permissions";
import { getProfileOrDefaultRosterTabs } from "./roster/roster-tabs";
import { createRpcClient } from "./rpc-client";
import { getEmployeeSortProp } from "./sorting";
import { hasNoShiftConflict, hour } from "./time";
import { TimeEntryDayIndex } from "./time-entry-index";
import { SYNC_EVENTS, TimeEntrySyncService } from "./time-entry-sync.service";
import { normalizeTimeEntry } from "./time-entry-write";
import { fillTimeEntryResults } from "./time/time-result";
import { getEmployeeOrderInDepartment, randomNumber } from "./utils";
import { version } from "./version";
export const APP_LAYOUTS = {
    dynamic: "Dynamisch",
    centered: "Zentriert",
};
export const DASHBOARD_CONFIG = {
    tiles: {
        birthdays: {
            label: "Geburtstage & Jubiläen",
            permissions: ["manage.employees.core_data"],
        },
        sickLeaveRequests: {
            label: "Krankmeldungen",
            permissions: ["manage.employees.absences"],
        },
        vacationRequests: {
            label: "Urlaubsanträge",
            permissions: ["manage.employees.absences"],
        },
        contractDeadlines: {
            label: "Vertragsfristen",
            permissions: ["manage.employees.contract"],
        },
        notes: {
            label: "Notizen",
            permissions: [],
        },
        tasks: {
            label: "Aufgaben",
            permissions: [],
        },
        shifts: {
            label: "Schichten des Tages",
            permissions: ["manage.planning.roster"],
        },
        savingsReport: {
            label: "Ersparniskurve",
            permissions: ["manage.reports.savings"],
        },
        recruitingApplications: {
            label: "Bewerbungseingang",
            permissions: ["manage.recruiting"],
            featureFlag: "recruitingDashboardTile",
        },
        timeLogComments: {
            label: "Schichtkommentare",
            permissions: ["manage.time"],
        },
    },
};
export const defaultDashboardConfig = {
    tiles: {
        birthdays: {
            order: 1,
            size: { inline: 1, block: 2 },
            enabled: true,
            settings: {
                lookaheadDays: 7,
                lookbehindDays: 0,
                showBirthdays: true,
                showAnniversaries: true,
            },
        },
        sickLeaveRequests: {
            order: 2,
            size: { inline: 1, block: 2 },
            enabled: true,
        },
        vacationRequests: {
            order: 3,
            size: { inline: 1, block: 2 },
            enabled: true,
        },
        contractDeadlines: {
            order: 4,
            size: { inline: 1, block: 2 },
            enabled: true,
            settings: {
                lookaheadDays: 30,
            },
        },
        notes: {
            order: 6,
            size: { inline: 2, block: 2 },
            enabled: true,
        },
        tasks: {
            order: 7,
            size: { inline: 2, block: 2 },
            enabled: true,
            settings: {
                removeSubtasks: false,
            },
        },
        savingsReport: {
            order: 8,
            size: { inline: 2, block: 2 },
            enabled: true,
        },
        recruitingApplications: {
            order: 9,
            size: { inline: 2, block: 2 },
            enabled: true,
        },
        shifts: {
            order: 10,
            size: { inline: 2, block: 2 },
            enabled: true,
        },
        timeLogComments: {
            order: 11,
            size: { inline: 1, block: 2 },
            enabled: true,
            settings: {
                lookbehindDays: 1,
            },
        },
    },
};
export class AppSettings {
    constructor() {
        this.rosterDisplayCosts = false;
        this.rosterDisplayTargets = false;
        this.rosterMirrorDepartments = true;
        this.rosterDisplayMode = "regular";
        this.rosterDisplayUnassigned = false;
        this.rosterDisplayAvailabilities = true;
        this.rosterDisplayTimeBalances = true;
        this.rosterDisplayNotes = true;
        this.rosterDisplaySuggestions = true;
        this.rosterDisplayFavSuggestionsOnly = false;
        this.rosterDisplayWeather = true;
        this.timeLogShowEvents = true;
        this.timeLogShowManual = true;
        this.contractsSortDirection = "descending";
        this.coreDataColumns = [];
        this.contractsColumns = [];
        this.dashboard = structuredClone(defaultDashboardConfig);
        this.layout = "dynamic";
    }
}
export class State {
    constructor() {
        this.session = null;
        this.account = null;
        this.settings = new AppSettings();
        this.updatedTimeEntries = new Map();
        this.removedTimeEntries = new Map();
        this.employeeFilters = new EmployeeFilters();
        this.termsDismissalTimestamp = null;
        this.oneSignalConsentGiven = null;
        this.oneSignalConsentTimestamp = null;
    }
}
__decorate([
    Type(() => SessionDTO),
    __metadata("design:type", Object)
], State.prototype, "session", void 0);
__decorate([
    Type(() => AccountDTO),
    __metadata("design:type", Object)
], State.prototype, "account", void 0);
__decorate([
    Type(() => AppSettings),
    __metadata("design:type", AppSettings)
], State.prototype, "settings", void 0);
__decorate([
    Type(() => TimeEntry),
    __metadata("design:type", Map)
], State.prototype, "updatedTimeEntries", void 0);
__decorate([
    Type(() => TimeEntry),
    __metadata("design:type", Map)
], State.prototype, "removedTimeEntries", void 0);
__decorate([
    Type(() => EmployeeFilters),
    __metadata("design:type", EmployeeFilters)
], State.prototype, "employeeFilters", void 0);
__decorate([
    Exclude(),
    Type(() => CompanyEntity),
    __metadata("design:type", Object)
], State.prototype, "company", void 0);
__decorate([
    Type(() => Date),
    __metadata("design:type", Object)
], State.prototype, "termsDismissalTimestamp", void 0);
__decorate([
    Type(() => Date),
    __metadata("design:type", Object)
], State.prototype, "oneSignalConsentTimestamp", void 0);
export class App {
    get account() {
        return this.state.account;
    }
    get company() {
        return this.state.company;
    }
    get profile() {
        if (!this.account || !this.company) {
            return null;
        }
        const profile = this.company.employees.find((e) => e.accountId === this.account.id);
        if (profile) {
            return profile;
        }
        // A company refetch replaces state.company, so a stand-in created per access would discard
        // everything the session wrote to it (e.g. newly created roster tabs).
        const key = `${this.account.id}:${this.company.id}`;
        if (!this._standInProfile || this._standInProfileKey !== key) {
            this._standInProfileKey = key;
            this._standInProfile = new Employee({
                accountId: this.account.id,
                firstName: this.account.firstName,
                lastName: this.account.lastName,
                email: this.account.email,
                positions: [],
                contracts: [],
            });
        }
        this.company.employees.push(this._standInProfile);
        return this._standInProfile;
    }
    get venues() {
        return this.company?.venues || [];
    }
    get accessibleVenues() {
        return this.venues.filter((venue) => this.hasAccess({ venue }));
    }
    get departments() {
        return this.venues.flatMap((v) => v.departments);
    }
    get accessibleDepartments() {
        return this.departments.filter((department) => this.hasAccess({ department }));
    }
    get positions() {
        return this.departments.flatMap((d) => d.positions);
    }
    get archivedPositions() {
        let archivedPositions = [];
        if (!this.company) {
            return [];
        }
        const allVenues = [...this.company.venues, ...this.company.archivedVenues];
        for (const venue of allVenues) {
            const allDepartments = [...venue.departments, ...venue.archivedDepartments];
            for (const department of allDepartments) {
                archivedPositions = archivedPositions.concat(department.archivedPositions);
            }
        }
        return archivedPositions;
    }
    get accessiblePositions() {
        return this.positions.filter((position) => this.hasAccess({ position }));
    }
    get employees() {
        return (this.company?.employees.sort((a, b) => (a.lastName > b.lastName ? 1 : a.lastName < b.lastName ? -1 : 0)) ||
            []);
    }
    get accessibleEmployees() {
        return this.employees.filter((employee) => this.hasAccess({ employee }));
    }
    get activeAccessibleEmployees() {
        return this.accessibleEmployees.filter((e) => e.active);
    }
    get shiftTemplates() {
        return (this.profile && this.profile.shiftTemplates) || [];
    }
    set shiftTemplates(shiftTemplates) {
        if (this.profile) {
            this.profile.shiftTemplates = shiftTemplates;
            void this.updateAccount({
                profile: { shiftTemplates },
            });
        }
    }
    get saveableRosterTabs() {
        if (this._saveableRosterTabsCache) {
            return this._saveableRosterTabsCache;
        }
        if (!this.profile || !this.company) {
            return [];
        }
        this._saveableRosterTabsCache = getProfileOrDefaultRosterTabs(this.profile.rosterTabs, this.accessibleVenues.filter((venue) => !venue.archived));
        return this._saveableRosterTabsCache;
    }
    // A fresh array (never the cached saveableRosterTabs instance) so a consumer that reorders it
    // in place — e.g. the sortable tab list — can't corrupt the cache.
    get rosterTabs() {
        return this.temporaryRosterTab
            ? [...this.saveableRosterTabs, this.temporaryRosterTab]
            : [...this.saveableRosterTabs];
    }
    async showDepartmentsInRoster(params) {
        if (!this.profile) {
            return;
        }
        const venue = this.venues.find((v) => v.id === params.venueId);
        const allDepartmentIds = venue?.departments.map((d) => d.id) || [];
        const isAllDepartments = params.departmentIds.length >= allDepartmentIds.length &&
            allDepartmentIds.every((id) => params.departmentIds.includes(id));
        const isSubset = (array1, array2) => array2.every((element) => array1.includes(element));
        const existingIndex = this.rosterTabs.findIndex((tab) => {
            if (isAllDepartments) {
                return !tab.departments || tab.departments.length === 0;
            }
            return (isSubset(tab.departments || [], params.departmentIds) &&
                isSubset(params.departmentIds, tab.departments || []));
        });
        if (existingIndex !== -1) {
            return existingIndex;
        }
        this.temporaryRosterTab = {
            name: "Temporärer Tab (Yna)",
            venue: params.venueId,
            departments: isAllDepartments ? undefined : params.departmentIds,
        };
        this.publish();
        return this.rosterTabs.findIndex((tab) => tab === this.temporaryRosterTab);
    }
    get settings() {
        return this.state.settings;
    }
    get availableTimeSettings() {
        if (!this.company) {
            return [];
        }
        return this.company.settings.availableTimeSettings.sort((a, b) => a.order - b.order);
    }
    get employeeFilters() {
        return this.state.employeeFilters;
    }
    get locale() {
        return this.company?.country || "DE";
    }
    get localized() {
        return Localized[this.locale];
    }
    constructor(storage, serverUrl) {
        this.storage = storage;
        this.state = new State();
        this.temporaryRosterTab = null;
        // saveableRosterTabs rebuilt fresh tab objects on every access, so consumers saw a "changed"
        // tab each read. Cache and invalidate in publish() (the choke point for every mutation that can
        // affect them).
        this._saveableRosterTabsCache = null;
        this._standInProfile = null;
        this._standInProfileKey = null;
        this.hasLoaded = false;
        this._subscribers = [];
        this._refreshableTargetCallbacks = new Map();
        this._timeEntryDayIndex = new TimeEntryDayIndex();
        this.api = createRpcClient(serverUrl, () => {
            return {
                version: version, // TODO: replace with fullVersion for higher precision
                userAgent: navigator.userAgent,
                currentUrl: window.location.href,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                location: this.state.clientInfo?.location,
            };
        });
        this._timeEntrySyncService = new TimeEntrySyncService(this.api, this.state);
        // Listen for sync events and save state
        this._timeEntrySyncService.on(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED, () => {
            this.save().catch((e) => {
                console.error(e);
            });
        });
        this.loaded = this.load();
    }
    async load() {
        // load state from storage
        try {
            const state = await this.storage.get(State, "State", "state");
            await this.update(state);
            if (this.account && this.state.session && this.state.session.scope !== "admin") {
                await Promise.all([this.fetchAccount(), this.fetchCompany()]);
                this._timeEntrySyncService.throttledSyncTimeEntries();
            }
        }
        catch (e) {
            await this.update();
        }
        if (!this.state.employeeFilters) {
            this.resetEmployeeFilters();
        }
        this.hasLoaded = true;
    }
    async save() {
        if (this.state.session) {
            // @ts-expect-error 2790
            delete this.state.session.account;
        }
        await this.storage.set("State", "state", this.state);
    }
    subscribe(fn) {
        if (this._subscribers.indexOf(fn) === -1) {
            this._subscribers.push(fn);
        }
    }
    unsubscribe(fn) {
        const index = this._subscribers.indexOf(fn);
        if (index !== -1) {
            this._subscribers.splice(index, 1);
        }
    }
    registerRefreshableTarget(type, fn) {
        const targetCallbacks = this._refreshableTargetCallbacks.get(type) || [];
        if (targetCallbacks.indexOf(fn) === -1) {
            targetCallbacks.push(fn);
        }
        this._refreshableTargetCallbacks.set(type, targetCallbacks);
    }
    removeRefreshableTarget(type, fn) {
        const targetCallbacks = this._refreshableTargetCallbacks.get(type);
        if (targetCallbacks) {
            const index = targetCallbacks.indexOf(fn);
            if (index !== -1) {
                targetCallbacks.splice(index, 1);
            }
        }
    }
    refreshTarget(type) {
        const targetCallbacks = this._refreshableTargetCallbacks.get(type);
        if (!targetCallbacks) {
            return;
        }
        for (const fn of targetCallbacks) {
            try {
                fn(type);
            }
            catch (error) {
                console.error("Error emitting change event", error);
            }
        }
    }
    publish() {
        this._saveableRosterTabsCache = null;
        for (const fn of this._subscribers) {
            fn(this.state);
        }
    }
    async update(changes) {
        if (changes) {
            Object.assign(this.state, changes);
        }
        this.api.session = this.state.session;
        this.publish();
        await this.save();
    }
    async updateSettings(vals) {
        Object.assign(this.state.settings, vals);
        this.publish();
        await this.save();
    }
    async login(params) {
        const session = (this.api.session = await this.api.login(new LoginParams(params)));
        const [account, company] = await Promise.all([
            this.api.me(),
            session?.companyId
                ? this.api.getCompany(new GetCompanyParams({ id: session.companyId }))
                : Promise.resolve(undefined),
        ]);
        await this.update({ session, account, company: company || undefined });
    }
    async termsNeedToBeAccepted() {
        const lastDismissal = this.state.termsDismissalTimestamp || new Date(0);
        if (Date.now() - lastDismissal.getTime() < hour * 20) {
            return;
        }
        try {
            const termsState = await this.api.getTermsAndConditionsState();
            if (!termsState || (termsState.termsAccepted && termsState.processingAgreementAccepted)) {
                return;
            }
            return termsState;
        }
        catch (e) {
            console.warn("Error fetching terms and conditions state", e);
        }
        return;
    }
    async selectCompany(companyId) {
        const session = await this.api.selectCompany(companyId);
        this._timeEntrySyncService.clearSyncState();
        this._timeEntryDayIndex.clear();
        await this.update({ session, company: undefined, employeeFilters: new EmployeeFilters() });
    }
    async logout() {
        if (this.state.session) {
            try {
                await this.api.revokeSession();
            }
            catch (e) {
                console.error(e);
            }
        }
        const settings = this.state.settings;
        const state = new State();
        state.settings = settings;
        this._timeEntryDayIndex.clear();
        await this.update(state);
    }
    async fetchAccount() {
        const account = await this.api.me();
        await this.update({ account });
    }
    async fetchCompany() {
        const company = (this.state.session?.companyId &&
            (await this.api.getCompany(new GetCompanyParams({ id: this.state.session.companyId })))) ||
            undefined;
        await this.update({ company });
    }
    async updateAccount(params) {
        await this.api.updateAccount(new UpdateAccountParams(params));
        await this.fetchAccount();
        if (params.profile) {
            await this.fetchCompany();
        }
    }
    async updateRosterTabs() {
        if (!this.profile) {
            return;
        }
        await this.updateAccount({ profile: { rosterTabs: this.saveableRosterTabs } });
    }
    async updateCompany(params) {
        await this.api.updateCompany(new UpdateCompanyParams(params));
        await this.fetchCompany();
    }
    async createVenue(params) {
        const venue = await this.api.createVenue(new CreateVenueParams(params));
        await this.fetchCompany();
        return venue;
    }
    async updateVenue(params) {
        const venue = await this.api.updateVenue(new UpdateVenueParams(params));
        await this.fetchCompany();
        return venue;
    }
    async archiveVenue({ id }) {
        await this.api.archiveVenue(id);
        await this.fetchCompany();
    }
    async transferVenue(params) {
        await this.api.transferVenue(new TransferVenueDTO(params));
        await this.fetchCompany();
    }
    async createDepartment(params) {
        const dep = await this.api.createDepartment(new CreateDepartmentParams({ ...params }));
        await this.fetchCompany();
        return dep;
    }
    async updateDepartment(params) {
        const dep = await this.api.updateDepartment(new UpdateDepartmentParams({ ...params }));
        await this.fetchCompany();
        return dep;
    }
    getDepartment(id) {
        return (this.company && this.company.getDepartment(id)) || { venue: null, department: null };
    }
    getPosition(id) {
        return (this.company && this.company.getPosition(id)) || null;
    }
    getTimeSettings(context) {
        return this.company?.getTimeSettings(context) || new TimeSettings();
    }
    // TODO: move to tracking.ts
    getUnplannedShiftPositions(employee, venueId, departments) {
        const positions = [];
        for (const position of employee.positions.filter((p) => !departments || departments.includes(p.departmentId))) {
            const { venue, department } = this.getDepartment(position.departmentId);
            if (!venue ||
                !department ||
                (venueId && venue.id !== venueId) ||
                (departments && !departments.includes(department.id))) {
                continue;
            }
            const { unplannedCheckins, trackingEnabled } = this.getTimeSettings({ employee, position });
            if (trackingEnabled && unplannedCheckins) {
                positions.push({ department, position });
            }
        }
        return positions;
    }
    async archiveDepartment(id) {
        await this.api.archiveDepartment(id);
        await this.fetchCompany();
    }
    async createEmployee(params) {
        const employee = await this.api.createEmployee(new CreateEmployeeParams(params));
        await this.fetchCompany();
        return employee;
    }
    async updateEmployee({ id }, params, refresh = true) {
        const employee = await this.api.updateEmployee(new UpdateEmployeeParams({ ...params, id }));
        if (refresh) {
            await this.fetchCompany();
        }
        return employee;
    }
    // TODO: use findEmployeeById here
    getEmployee(id) {
        return (this.company && this.company.employees.find((e) => e.id === id)) || null;
    }
    getVenue(venueId) {
        return this.company?.getVenue(venueId) || null;
    }
    // TODO: move to roster or company utils
    getEmployeesForDepartment(dep, employees = this.employees) {
        if (!this.company) {
            return [];
        }
        const employeesInDepartment = employees.filter((emp) => emp.positions.some((p) => p.departmentId === dep.id));
        return sortBy(employeesInDepartment, (employee) => {
            return getEmployeeOrderInDepartment(employee, dep);
        }, "ascending");
    }
    async generatePin() {
        let pin;
        // Randomly generate pin until we got a unique one
        do {
            pin = randomNumber(0, 9999).toString().padStart(4, "0");
        } while (this.company.employees.some((e) => e.timePin === pin));
        return pin;
    }
    async getLiveKitAuthToken() {
        return await this.api.getLiveKitAuthToken();
    }
    _normalizeEntry(entry, options) {
        normalizeTimeEntry(entry, this.company, options);
    }
    async createOrUpdateTimeEntries(entries, { applyAutoMeals = false, wait = false, publish = false, setUpdated = true, } = {}) {
        // Normalize entries before passing to service
        const entriesArray = Array.isArray(entries) ? entries : [entries];
        const otherEntries = this._timeEntryDayIndex.contextFor(entriesArray);
        for (const entry of entriesArray) {
            this._normalizeEntry(entry, { applyAutoMeals, otherEntries });
            if (entry.type === TimeEntryType.Work) {
                entry.result = null;
                entry.resultPlanned = null;
                entry.resultMaxTotalCosts = null;
            }
        }
        this._fillWorkEntryResults([...entriesArray, ...otherEntries]);
        const written = await this._timeEntrySyncService.createOrUpdateTimeEntries(entries, {
            wait,
            publish,
            setUpdated,
        });
        this._timeEntryDayIndex.put(entriesArray);
        return written;
    }
    onTimeEntriesSynced(handler) {
        this._timeEntrySyncService.on(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED, handler);
    }
    offTimeEntriesSynced(handler) {
        this._timeEntrySyncService.off(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED, handler);
    }
    hasPendingTimeEntryWrites(employeeId) {
        return [...this.state.updatedTimeEntries.values(), ...this.state.removedTimeEntries.values()].some((e) => e.employeeId === employeeId);
    }
    async removeTimeEntries(entries, wait = false, publish = false) {
        const entriesArray = Array.isArray(entries) ? entries : [entries];
        this._timeEntryDayIndex.remove(entriesArray);
        return this._timeEntrySyncService.removeTimeEntries(entries, wait, publish);
    }
    async syncTimeEntries() {
        return this._timeEntrySyncService.syncTimeEntries();
    }
    async getTimeEntries(params) {
        return this._resolveTimeEntries(await this.api.getTimeEntries(new GetTimeEntriesParams(params)));
    }
    async getRosterTimeEntries(params) {
        const entries = this._resolveTimeEntries(await this.api.getRosterTimeEntries(new GetTimeEntriesParams(params)));
        this._fillWorkEntryResults(entries);
        return entries;
    }
    async getRosterVenueEmployeeIds(params) {
        return this.api.getRosterVenueEmployeeIds(new GetTimeEntriesParams(params));
    }
    _resolveTimeEntries(entries) {
        const allPositions = [...this.positions, ...this.archivedPositions];
        const positionMap = new Map(allPositions.map((p) => [p.id, p]));
        entries.forEach((entry) => {
            entry.position = (entry.positionId && positionMap.get(entry.positionId)) || null;
        });
        const resolved = entries
            .filter((entry) => !this._timeEntrySyncService.isRemoved(entry))
            .map((entry) => this.state.updatedTimeEntries.get(entry.id) || entry);
        this._timeEntryDayIndex.put(resolved);
        return resolved;
    }
    _fillWorkEntryResults(entries) {
        const company = this.company;
        if (!company) {
            return;
        }
        const workByDay = new Map();
        for (const entry of entries) {
            if (entry.type === TimeEntryType.Work && entry.employeeId) {
                const key = `${entry.employeeId}:${entry.date}`;
                let list = workByDay.get(key);
                if (!list) {
                    list = [];
                    workByDay.set(key, list);
                }
                list.push(entry);
            }
        }
        for (const entry of entries) {
            if (entry.type !== TimeEntryType.Work || !entry.employeeId || entry.result) {
                continue;
            }
            const employee = this.getEmployee(entry.employeeId);
            if (!employee) {
                continue;
            }
            fillTimeEntryResults(entry, company.settings, company, employee, workByDay.get(`${entry.employeeId}:${entry.date}`) ?? []);
        }
    }
    hasPermission(scope) {
        return !!this.account && !!this.company && this.company.hasPermission(this.account, scope);
    }
    hasPermissionForEmployee(employee, scope) {
        return this.hasAccess({ employee }) && (!scope || this.hasPermission(scope));
    }
    hasAccess(context) {
        return Boolean(this.account &&
            this.profile &&
            this.company &&
            (this.account.admin || this.company.hasAccess(this.profile, context)));
    }
    /**
     * returns all permissions related to the given permission
     * `requires` are all permissions that require the given permission
     * `sub` are all permissions that are sub-permissions of the given permission
     * `sub` also includes the given permission itself
     */
    getRelatedPermissions(key, scopes = PERMISSIONS.children) {
        const res = {
            requires: [],
            sub: [],
        };
        for (const scope of scopes) {
            if (scope.requires?.includes(key) && scope.key) {
                res.requires.push(scope.key);
            }
            if (scope.key?.startsWith(key)) {
                res.sub.push(scope.key);
            }
            if (scope.children) {
                const { requires, sub } = this.getRelatedPermissions(key, scope.children);
                res.requires.push(...requires);
                res.sub.push(...sub);
            }
        }
        return res;
    }
    isUpdated(a) {
        return this._timeEntrySyncService.isUpdated(a);
    }
    isRemoved(a) {
        return this._timeEntrySyncService.isRemoved(a);
    }
    getPositionColor(position) {
        return this.company?.getPositionColor(position);
    }
    getTimeEntryColor(entry) {
        return this.company?.getTimeEntryColor(entry);
    }
    getTimeEntryLabel(entry) {
        const position = entry.positionId ? this.getPosition(entry.positionId)?.position : null;
        return position?.name || this.company.getTimeEntryTypeLabel(entry);
    }
    isAvailable(entry, employee, otherEntries, absences, requirePublished = true) {
        const contract = employee.getContractForDate(entry.date);
        return (
        // takeShift rejects past-dated shifts, so don't advertise them
        !entry.isPast &&
            !entry.startFinal &&
            !entry.endFinal &&
            employee.access?.permissions.includes("staff.roster.take") &&
            !!employee.accountId &&
            (!entry.employeeId ||
                (entry.employeeId !== employee.id && entry.offered && entry.offered > entry.updated)) &&
            (!requirePublished || entry.isPublished) &&
            !entry.deleted &&
            employee.positions.some((p) => p.id == entry.position?.id) &&
            hasNoShiftConflict(entry, employee.id, otherEntries) &&
            !absences.some((a) => a.employeeId === employee.id && a.start <= entry.date && a.end > entry.date) &&
            !!contract &&
            !contract.blocked);
    }
    updateEmployeeFilters(upd) {
        void this.update({
            employeeFilters: Object.assign(this.employeeFilters, upd),
        });
    }
    resetEmployeeFilters() {
        this.updateEmployeeFilters(new EmployeeFilters());
    }
    employeeMatchesFilters(employee, date) {
        const { text, filters } = this.employeeFilters;
        return (`${employee.name} ${employee.staffNumber}`.toLowerCase().includes(text.toLowerCase()) &&
            matchesFilters(filters, { company: this.company, employee, date }));
    }
    getFilteredEmployees(range) {
        const filteredEmployees = this.accessibleEmployees.filter((e) => this.employeeMatchesFilters(e, range));
        const sortedEmployees = sortBy(filteredEmployees, getEmployeeSortProp(this.employeeFilters.sortProperty), this.employeeFilters.sortDirection);
        return sortedEmployees;
    }
}
//# sourceMappingURL=app.js.map